
import { User, UserConfig, PlanType, SystemSettings, EmailJsConfig, AnalyticsEvent, Appointment, GlobalPricing, BillingProfile, Payment, UserProfile, DocumentType, Document, DocumentNote } from '../types';
import { auth, db, storage, firebaseConfig, app } from './firebase';
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import { getFirestore, collection, query, where, getCountFromServer, orderBy, getDocs } from "firebase/firestore";
import { ParsedClient } from '../src/utils/csvValidation';

export const api = {

  login: async (email: string, password: string): Promise<User> => {
    try {
      const userCredential = await auth.signInWithEmailAndPassword(email, password);
      const uid = userCredential.user!.uid;

      const userDocRef = db.collection("users").doc(uid);
      const userDocSnap = await userDocRef.get();

      if (!userDocSnap.exists) {
        const newUser = {
          email: email,
          role: email.includes('admin') ? 'admin' : 'user',
          plan: PlanType.FREE,
          subscriptionStatus: 'active',
          subscriptionEnd: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        };
        await userDocRef.set(newUser);
        return { id: uid, ...newUser, config: getDefaultConfig(email) } as User;
      }

      const userData = userDocSnap.data();

      if (userData?.subscriptionStatus === 'inactive') {
        await auth.signOut();
        throw new Error("Your account is inactive. Please contact your agency.");
      }

      const configDocRef = db.collection("configs").doc(uid);
      const configDocSnap = await configDocRef.get();
      const configData = configDocSnap.exists ? configDocSnap.data() : getDefaultConfig(email);

      let agencyPlan: PlanType | undefined = undefined;
      if (userData?.role === 'agency_member' && userData?.agencyId) {
        const agencyDocSnap = await db.collection("users").doc(userData.agencyId).get();
        if (agencyDocSnap.exists) {
          agencyPlan = agencyDocSnap.data()?.plan as PlanType;
        }
      }

      // Handle legacy roles if needed, or default
      return {
        id: uid,
        ...userData,
        role: userData?.role || 'user',
        config: configData,
        emailVerified: userCredential.user?.emailVerified,
        agencyPlan
      } as User;
    } catch (error: any) {
      console.error("Login Error:", error);
      throw new Error(error.message);
    }
  },

  loginWithGoogle: async (): Promise<User> => {
    try {
      const provider = new firebase.auth.GoogleAuthProvider();
      const result = await auth.signInWithPopup(provider);
      const idToken = await result.user!.getIdToken(true);

      // Verify with backend and get full user data
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token: idToken })
      });

      if (!response.ok) {
        throw new Error('Google Authentication Failed on Server');
      }

      const userData = await response.json();
      return { ...userData, emailVerified: true } as User; // Google users are verified

    } catch (error: any) {
      console.error("Google Login Error:", error);
      throw new Error(error.message);
    }
  },

  getUser: async (userId: string): Promise<User> => {
    const doc = await db.collection("users").doc(userId).get();
    if (!doc.exists) throw new Error("User not found");
    const data = doc.data() as User;
    const configSnap = await db.collection("configs").doc(userId).get();
    const configData = configSnap.exists ? configSnap.data() : getDefaultConfig(data?.email);
    // If getting current user, check auth status
    let isVerified = false;
    if (auth.currentUser && auth.currentUser.uid === userId) {
      isVerified = auth.currentUser.emailVerified;
    }

    let agencyPlan: PlanType | undefined = undefined;
    if (data.role === 'agency_member' && data.agencyId) {
      const agencyDoc = await db.collection("users").doc(data.agencyId).get();
      if (agencyDoc.exists) {
        agencyPlan = agencyDoc.data()?.plan as PlanType;
      }
    }

    return { id: userId, ...data, config: configData, emailVerified: isVerified, agencyPlan } as User;
  },

  register: async (email: string, password: string, language: string = 'en', companyName: string = 'My Company'): Promise<User> => {
    try {
      const userCredential = await auth.createUserWithEmailAndPassword(email, password);
      const uid = userCredential.user!.uid;

      // Normalize language (e.g. 'el-GR' -> 'el')
      const normalizedLang = (language || 'en').substring(0, 2).toLowerCase();

      const defaultConfig = getDefaultConfig(email, normalizedLang, companyName);

      // Create User Record
      // Determine role based on email or default to 'agency'
      const role = email.includes('admin') ? 'admin' : 'agency';

      // Infer Timezone and Currency
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      let currency = 'USD';
      let dateFormat = 'M/d/yyyy';

      if (normalizedLang === 'el') {
        currency = 'EUR';
        dateFormat = 'd/M/yyyy';
      } else if (normalizedLang === 'en-GB' || normalizedLang === 'en-IE') {
        currency = 'GBP'; // Or EUR for IE
        dateFormat = 'd/M/yyyy';
      }

      // Create User Record
      const newUser: User = {
        id: uid,
        email: email,
        role: role,
        plan: PlanType.ADVANCED,
        createdAt: new Date().toISOString(),
        subscriptionStatus: 'active',
        subscriptionEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        config: defaultConfig, // Include config to satisfy User interface,
        timezone: timezone,
        currency: currency,
        dateFormat: dateFormat,
        language: normalizedLang,
        profile: {
          companyName: companyName,
          vatNumber: '',
          responsiblePerson: '',
          taxOffice: '',
          activity: '',
          address: {
            street: '',
            city: '',
            postalCode: '',
            country: normalizedLang === 'el' ? 'GR' : 'US'
          },
          contact: { mobile: '', phone: '' },
          notes: ''
        }
      };

      await db.collection("users").doc(uid).set(newUser);
      await db.collection("configs").doc(uid).set(defaultConfig);

      // Send Welcome Email (via Backend)
      try {
        const token = await userCredential.user!.getIdToken();
        const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        console.log("Sending welcome email to:", `${API_BASE_URL}/api/send-welcome-email`);

        const response = await fetch(`${API_BASE_URL}/api/send-welcome-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ email, name: email.split('@')[0], language: normalizedLang })
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Welcome email failed. Status:", response.status, "Response:", errorText);
        } else {
          console.log("Welcome email sent successfully.");
        }
      } catch (error) {
        console.error("Failed to send welcome email (Network/Fetch error):", error);
        // We don't block registration if email fails
      }

      return {
        id: uid,
        ...newUser,
        ...newUser,
        config: defaultConfig,
        emailVerified: false // Newly registered via email/pass are not verified yet
      } as User;

    } catch (error: any) {
      console.error("Register Error:", error);
      throw new Error(error.message);
    }
  },

  sendPasswordResetLink: async (email: string): Promise<void> => {
    try {
      await auth.sendPasswordResetEmail(email);
    } catch (error: any) {
      console.error("Reset Error:", error);
      let message = error.message;
      if (error.code === 'auth/user-not-found') {
        message = "No account found with this email in Firebase Authentication. Please check the email or register.";
      }
      throw new Error(message);
    }
  },

  updateUserPassword: async (newPassword: string): Promise<void> => {
    if (auth.currentUser) {
      try {
        await auth.currentUser.updatePassword(newPassword);
      } catch (error: any) {
        if (error.code === 'auth/requires-recent-login') {
          throw new Error("For security, please logout and login again before changing your password.");
        }
        throw error;
      }
    } else {
      throw new Error("No user logged in");
    }
  },

  updateUserEmail: async (newEmail: string): Promise<void> => {
    if (auth.currentUser) {
      try {
        await auth.currentUser.updateEmail(newEmail);
        const uid = auth.currentUser.uid;
        await db.collection("users").doc(uid).update({ email: newEmail });
        await db.collection("configs").doc(uid).update({ contactEmail: newEmail });
      } catch (error: any) {
        if (error.code === 'auth/requires-recent-login') {
          throw new Error("For security, please logout and login again before changing your email.");
        } else if (error.code === 'auth/email-already-in-use') {
          throw new Error("This email is already in use by another account.");
        }
        throw error;
      }
    } else {
      throw new Error("No user logged in");
    }
  },

  getAllUsers: async (): Promise<User[]> => {
    const userSnapshot = await db.collection("users").get();
    const usersList: User[] = [];
    for (const uDoc of userSnapshot.docs) {
      const userData = uDoc.data();
      const configDoc = await db.collection("configs").doc(uDoc.id).get();
      usersList.push({
        id: uDoc.id,
        ...userData,
        config: configDoc.exists ? configDoc.data() as UserConfig : getDefaultConfig(userData.email)
      } as User);
    }
    return usersList;
  },

  // Used by Admin to create users
  createUser: async (email: string, plan: PlanType): Promise<User> => {
    // Use unique name to prevent collisions
    const appName = `Secondary-${Date.now()}-${Math.random()}`;
    const secondaryApp = firebase.initializeApp(firebaseConfig, appName);
    const secondaryAuth = secondaryApp.auth();

    try {
      const tempPassword = "password123";
      const userCredential = await secondaryAuth.createUserWithEmailAndPassword(email, tempPassword);
      const uid = userCredential.user!.uid;
      const newUser = {
        email: email,
        role: 'user',
        plan: plan,
        subscriptionStatus: 'active',
        subscriptionEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      };
      await db.collection("users").doc(uid).set(newUser);
      const defaultConfig = getDefaultConfig(email);
      await db.collection("configs").doc(uid).set(defaultConfig);

      await secondaryApp.delete();

      alert(`User created successfully!\nEmail: ${email}\nTemporary Password: ${tempPassword}`);
      return { id: uid, ...newUser, config: defaultConfig } as User;
    } catch (e: any) {
      try { await secondaryApp.delete(); } catch { }
      throw new Error(e.message || "Failed to create user");
    }
  },

  // --- AGENCY FEATURES ---

  getAgencyClients: async (agencyId: string, assignedAgentId?: string, includeMembers: boolean = false): Promise<User[]> => {
    try {
      let query;

      if (assignedAgentId) {
        // Optimization: Query directly by assignment to avoid Composite Index (AgencyId + AssignedIds) requirements.
        // Assigned Agent IDs are unique enough and this query uses the default Single Field Index.
        query = db.collection("users").where("assignedAgentIds", "array-contains", assignedAgentId);
      } else {
        query = db.collection("users").where("agencyId", "==", agencyId);
      }

      const snapshot = await query.get();
      console.log(`[getAgencyClients] Fetching for agency ${agencyId}, assignedTo: ${assignedAgentId}. Found: ${snapshot.size}`);

      const clients: User[] = [];
      for (const doc of snapshot.docs) {
        const data = doc.data();
        // Filter out Agency Members (Employees) unless specifically requested
        if (!includeMembers && data.role === 'agency_member') continue;

        const configSnap = await db.collection("configs").doc(doc.id).get();
        clients.push({
          id: doc.id,
          ...data,
          // Safe handling for createdAt if it's a Firestore Timestamp
          createdAt: (data.createdAt && typeof data.createdAt.toDate === 'function')
            ? data.createdAt.toDate().toISOString()
            : (data.createdAt || new Date().toISOString()),
          config: configSnap.exists ? configSnap.data() : getDefaultConfig(data.email)
        } as User);
      }
      return clients;
    } catch (error: any) {
      console.error("Error fetching agency clients:", error);
      // alert("Error fetching clients: " + error.message); 
      // User reported that they see 0 clients, so maybe it's NOT an error but an empty list. 
      // But if it IS an error, we need to know.
      alert(`Debug Error: Failed to fetch clients. Reason: ${error.message || error.code}`);
      return [];
    }
  },

  createAgencyClient: async (agencyId: string, email: string, companyName?: string, responsiblePerson?: string, vatNumber?: string, sendInvitation?: boolean): Promise<{ user: User, password: string }> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/agency/clients`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ email, companyName, responsiblePerson, vatNumber, sendInvitation })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to create client');
    }

    const { user, password } = await response.json();
    return { user: user as User, password };
  },

  importClientsFromCsv: async (payload: { clients: ParsedClient[], sendInvites: boolean }): Promise<any> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/agency/import-clients`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to import clients');
    }

    return await response.json();
  },

  // --- MAPPING PROFILES ---
  saveMappingProfile: async (agencyId: string, name: string, mapping: Record<string, string>): Promise<string> => {
    const docRef = db.collection("agencies").doc(agencyId).collection("import_mapping_profiles").doc();
    await docRef.set({
      name,
      mapping,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  },

  getMappingProfiles: async (agencyId: string): Promise<{ id: string, name: string, mapping: Record<string, string> }[]> => {
    const snapshot = await db.collection("agencies").doc(agencyId).collection("import_mapping_profiles").orderBy('createdAt', 'desc').get();
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    })) as any;
  },

  deleteMappingProfile: async (agencyId: string, profileId: string): Promise<void> => {
    await db.collection("agencies").doc(agencyId).collection("import_mapping_profiles").doc(profileId).delete();
  },

  createAgencyMember: async (agencyId: string, data: { email: string, name: string, phone: string }): Promise<{ user: User, message: string }> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/agency/members`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to create member');
    }

    return await response.json();
  },



  resendInvitation: async (clientId: string): Promise<void> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/agency/clients/${clientId}/invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to resend invitation');
    }
  },

  deleteAgencyMember: async (memberId: string, agencyId: string): Promise<void> => {
    // Use Backend to delete Auth User + Firestore Data
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    // Get current user token for auth
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/agency/members/${memberId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to delete member');
    }
  },

  forceDeleteUserByEmail: async (email: string): Promise<void> => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/admin/force-delete-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to force delete user');
    }
  },

  deleteAgencyClient: async (clientId: string, agencyId: string): Promise<void> => {
    // 1. Delete all documents associated with the client (Files/Firestore references)
    // We keep this client-side for now as it handles Storage deletion which is complex to move without signed URLs or Admin SDK storage buckets fully setup
    try {
      const clientDocs = await api.getDocuments(agencyId, clientId);
      for (const doc of clientDocs) {
        await api.deleteDocument(doc.id, doc.storagePath);
      }
    } catch (e) {
      console.error("Error deleting client documents:", e);
      // Continue to delete the user even if documents fail
    }

    // 2. Call Backend to Delete User, Config, and Auth Record
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/agency/clients/${clientId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to delete client');
    }
  },



  // --- DOCUMENTS MODULE (AGENCY & CLIENT) ---

  // 1. Document Types (Agency Only)
  createDocumentType: async (type: Omit<DocumentType, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    const docRef = await db.collection("documentTypes").add({
      ...type,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return docRef.id;
  },

  getDocumentTypes: async (agencyId: string, isClientView: boolean = false): Promise<DocumentType[]> => {
    let query = db.collection("documentTypes").where("agencyId", "==", agencyId);

    // Convert to regular Firestore query before applying further filters
    // Note: In client-side SDK we chain directly.

    if (isClientView) {
      query = query.where("isVisibleToClient", "==", true);
    }

    const snapshot = await query.get();
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DocumentType));
  },

  createDefaultDocumentTypes: async (agencyId: string, plan: PlanType): Promise<{ created: number, skipped: number }> => {
    const DEFAULT_TYPES = [
      { name_el: 'ΦΠΑ', name_en: 'VAT', client_upload: false, ai_ocr: true, category: 'tax' },
      { name_el: 'ΕΦΚΑ', name_en: 'EFKA', client_upload: false, ai_ocr: true, category: 'tax' },
      { name_el: 'EXTRAIT ΤΡΑΠΕΖΩΝ', name_en: 'Bank Statements', client_upload: true, ai_ocr: false, category: 'financial' },
      { name_el: 'ΦΜΥ', name_en: 'Payroll Tax (FMY)', client_upload: false, ai_ocr: true, category: 'tax' },
      { name_el: 'ΙΣΟΛΟΓΙΣΜΟΙ', name_en: 'Balance Sheets', client_upload: false, ai_ocr: false, category: 'financial' },
      { name_el: 'ΣΥΜΒΟΛΑΙΑ', name_en: 'Contracts', client_upload: true, ai_ocr: false, category: 'legal' },
      { name_el: 'ΣΥΜΒΑΣΕΙΣ ΕΡΓΑΣΙΑΣ', name_en: 'Labor Contracts', client_upload: true, ai_ocr: false, category: 'legal' },
      { name_el: 'ΜΙΣΘΟΔΟΣΙΑ', name_en: 'Payroll', client_upload: false, ai_ocr: false, category: 'financial' },
      { name_el: 'ΑΛΛΟ', name_en: 'Other', client_upload: true, ai_ocr: false, category: 'other' },
      { name_el: 'ΑΔΕΙΕΣ ΠΡΟΣΩΠΙΚΟΥ', name_en: 'Other', client_upload: true, ai_ocr: false, category: 'other' },
      { name_el: 'ΦΟΡΟΣ ΕΙΣΟΔΗΜΑΤΟΣ', name_en: 'Income Tax', client_upload: false, ai_ocr: true, category: 'tax' },
      { name_el: 'ΤΕΛΟΣ ΕΠΙΤΗΔΕΥΜΑΤΟΣ', name_en: 'BUSINESS ACTIVITY FEE', client_upload: false, ai_ocr: true, category: 'tax' },
      { name_el: 'ΔΑΝΕΙΑΚΕΣ ΣΥΜΒΑΣΕΙΣ', name_en: 'LOAN AGREEMENTS', client_upload: false, ai_ocr: false, category: 'legal' },
      { name_el: 'ΝΟΜΙΜΟΠΟΙΗΤΙΚΑ', name_en: 'LEGAL CORPORATE DOCUMENTS', client_upload: false, ai_ocr: false, category: 'legal' },
      { name_el: 'Ε3', name_en: 'E3 FORM', client_upload: false, ai_ocr: false, category: 'financial' },
      { name_el: 'ΤΙΜΟΛΟΓΙΑ', name_en: 'Invoices', client_upload: true, ai_ocr: false, category: 'financial' },
      { name_el: 'ΑΠΟΤΕΛΕΣΜΑΤΑ ΧΡΗΣΗΣ', name_en: 'PROFIT - LOSS STATEMENT', client_upload: true, ai_ocr: false, category: 'financial' }
    ];

    // Check Plan for AI OCR
    // Assuming Agency and Advanced plans support AI OCR. 
    // PlanType enum: FREE, LIGHT, ADVANCED, AGENCY
    const hasAiOcrSupport = plan === PlanType.AGENCY || plan === PlanType.ADVANCED;

    // Fetch existing types to strictly avoid duplicates
    const existingTypes = await api.getDocumentTypes(agencyId);
    const existingNames = new Set(existingTypes.flatMap(t => [
      t.name.toLowerCase(),
      t.nameTranslations?.en?.toLowerCase(),
      t.nameTranslations?.el?.toLowerCase()
    ].filter(Boolean)));

    let createdCount = 0;
    let skippedCount = 0;

    const batch = db.batch();

    for (const type of DEFAULT_TYPES) {
      const isDuplicate = existingNames.has(type.name_el.toLowerCase()) || existingNames.has(type.name_en.toLowerCase());

      if (isDuplicate) {
        skippedCount++;
        continue;
      }

      const newDocRef = db.collection("documentTypes").doc();
      const newDoc: Omit<DocumentType, 'id'> = {
        agencyId,
        name: type.name_en, // Default internal name
        nameTranslations: {
          en: type.name_en,
          el: type.name_el
        },
        description: 'System generated',
        category: type.category as any,
        allowedFileTypes: ['pdf', 'jpg', 'png', 'jpeg'],
        isRequired: false,
        isVisibleToClient: true,
        clientCanUpload: type.client_upload,
        aiOcrEnabled: hasAiOcrSupport ? type.ai_ocr : false,
        active: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      batch.set(newDocRef, newDoc);
      createdCount++;
    }

    if (createdCount > 0) {
      await batch.commit();
    }

    return { created: createdCount, skipped: skippedCount };
  },

  // Fetch Public Profile (Company Name) via Backend (Bypasses Firestore Rules)
  getPublicProfile: async (userId: string): Promise<{ companyName: string }> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    // Fallback URL if env var is missing during dev
    const baseUrl = API_BASE_URL.startsWith('http') ? API_BASE_URL : 'http://localhost:3001';

    const response = await fetch(`${baseUrl}/api/public/profile/${userId}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch public profile');
    }
    return response.json();
  },

  updateDocumentType: async (id: string, updates: Partial<DocumentType>): Promise<void> => {
    await db.collection("documentTypes").doc(id).update({
      ...updates,
      updatedAt: new Date().toISOString()
    });
  },

  deleteDocumentType: async (id: string): Promise<void> => {
    // Hard delete for now, or soft delete if preferred
    await db.collection("documentTypes").doc(id).delete();
  },

  // 2. Documents (Instances)
  createDocument: async (doc: Omit<Document, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> => {
    const docRef = await db.collection("documents").add({
      ...doc,
      status: 'pending', // Always pending on creation
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });
    return docRef.id;
  },

  getDocuments: async (agencyId: string, clientId?: string): Promise<Document[]> => {
    let query = db.collection("documents").where("agencyId", "==", agencyId);

    if (clientId) {
      query = query.where("clientId", "==", clientId);
    }

    const snapshot = await query.get();
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Document));
  },

  updateDocumentStatus: async (id: string, status: 'approved' | 'rejected' | 'archived', moveContext?: { agencyName: string, clientName: string }): Promise<void> => {
    const docRef = db.collection("documents").doc(id);

    // 1. If approving and context provided, try to move file
    if (status === 'approved' && moveContext) {
      console.log(`[Approve] Attempting to move file for doc ${id}`, moveContext);
      const docSnap = await docRef.get();
      const docData = docSnap.data() as Document;

      if (docData && (docData.storagePath.startsWith('inbox/') || docData.uploadedByRole === 'client')) {
        try {
          const oldPath = docData.storagePath;

          // Use robust sanitizer
          const sanitize = (await import('../utils/textUtils')).sanitizeForStorage;
          const agencyFolder = sanitize(moveContext.agencyName);
          const clientFolder = sanitize(moveContext.clientName);
          const typeFolder = sanitize(docData.typeName || 'Other');
          const year = new Date().getFullYear();
          const filename = oldPath.split('/').pop();

          const newPath = `documents/${agencyFolder}/${clientFolder}/${typeFolder}/${year}/${filename}`;
          console.log(`[Approve] Moving from ${oldPath} to ${newPath}`);

          const oldRef = storage.ref().child(oldPath);
          const newRef = storage.ref().child(newPath);

          const url = await oldRef.getDownloadURL();
          console.log(`[Approve] Download URL: ${url}`);

          // PROXY FIX: Use local proxy to bypass CORS in development
          let fetchUrl = url;
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            fetchUrl = url.replace('https://firebasestorage.googleapis.com', '/firebase-storage-proxy');
            console.log(`[Approve] Using Proxy URL: ${fetchUrl}`);
          }

          const response = await fetch(fetchUrl);
          const blob = await response.blob();

          // CRITICAL: Must include metadata for Storage Rules
          const metadata = {
            customMetadata: {
              agencyId: docData.agencyId,
              clientId: docData.clientId
            }
          };

          console.log(`[Approve] Uploading to new path with metadata:`, metadata);
          await newRef.put(blob, metadata);
          const newDownloadUrl = await newRef.getDownloadURL();

          await docRef.update({
            status,
            updatedAt: new Date().toISOString(),
            storagePath: newPath,
            fileUrl: newDownloadUrl
          });
          console.log(`[Approve] File moved and doc updated successfully.`);

          // Delete old file
          await oldRef.delete().catch(e => console.warn("Failed to delete old file", e));
          return;

        } catch (moveError) {
          console.error("Failed to move file on approval:", moveError);
        }
      } else {
        console.warn(`[Approve] Condition skipped: `, {
          path: docData?.storagePath,
          role: docData?.uploadedByRole
        });
      }
    } else {
      if (status === 'approved') console.warn(`[Approve] Missing moveContext.`, moveContext);
    }

    // Default Update
    await docRef.update({
      status,
      updatedAt: new Date().toISOString()
    });
  },

  updateDocument: async (id: string, updates: Partial<Document> & { newStoragePath?: string }): Promise<void> => {
    // Use Backend API to handle potential file move and metadata update securely
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/agency/documents/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to update document');
    }
  },

  deleteDocument: async (id: string, storagePath?: string): Promise<void> => {
    // 1. Delete Storage File
    if (storagePath) {
      try {
        await storage.ref().child(storagePath).delete();
      } catch (e) {
        console.error("Failed to delete from storage", e);
        // We proceed to delete from Firestore even if storage delete fails
      }
    }

    // 2. Delete Notes Subcollection (Manually, as Firestore doesn't cascade)
    try {
      const notesSnapshot = await db.collection("documents").doc(id).collection("notes").get();
      if (!notesSnapshot.empty) {
        const batch = db.batch();
        notesSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        await batch.commit();
        console.log(`Deleted ${notesSnapshot.size} notes for document ${id}`);
      }
    } catch (e) {
      console.error("Failed to delete document notes:", e);
      // We proceed to delete the main document, though orphans might remain if this fails.
      // Ideally we shouldn't fail main delete for this.
    }

    // 3. Delete Document
    await db.collection("documents").doc(id).delete();
  },

  // --- CLIENT ATTENTION INDICATOR ---
  trackUserLogin: async (): Promise<void> => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const currentUser = auth.currentUser;
    if (!currentUser) return; // Silent return if not logged in
    const token = await currentUser.getIdToken();
    try {
      await fetch(`${API_BASE_URL}/api/user/track-login`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (e) {
      console.warn("Failed to track user login", e);
    }
  },

  testRecalculateAttention: async (): Promise<any> => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();
    const response = await fetch(`${API_BASE_URL}/api/agency/test-recalculate-attention`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to recalculate attention');
    }
    return response.json();
  },

  testSendAttentionEmail: async (): Promise<any> => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();
    const response = await fetch(`${API_BASE_URL}/api/agency/test-attention-email`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to send test email');
    }
    return response.json();
  },

  // --- DOCUMENT NOTES ---

  getDocumentNotes: async (documentId: string): Promise<DocumentNote[]> => {
    try {
      const snapshot = await db.collection("documents").doc(documentId).collection("notes").orderBy("createdAt", "asc").get();
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DocumentNote));
    } catch (error) {
      console.error("Error fetching document notes:", error);
      return [];
    }
  },

  addDocumentNote: async (documentId: string, note: Omit<DocumentNote, "id">): Promise<DocumentNote> => {
    try {
      const docRef = await db.collection("documents").doc(documentId).collection("notes").add(note);

      try {
        const updateData: any = {
          "metadata.notes": true
        };

        if (note.isAgency) {
          updateData["metadata.hasUnreadNotesForClient"] = true;
        } else {
          updateData["metadata.hasUnreadNotesForAgency"] = true;
        }

        await db.collection("documents").doc(documentId).update(updateData);
      } catch (updateError) {
        console.warn("Could not update document metadata for notes indicator:", updateError);
        // We proceed because the note itself was saved successfully
      }

      return { id: docRef.id, ...note };
    } catch (error) {
      console.error("Error adding document note:", error);
      throw error;
    }
  },

  markDocumentNotesAsRead: async (documentId: string, isAgency: boolean): Promise<void> => {
    try {
      const fieldToUpdate = isAgency ? "metadata.hasUnreadNotesForAgency" : "metadata.hasUnreadNotesForClient";
      await db.collection("documents").doc(documentId).update({
        [fieldToUpdate]: false
      });
    } catch (error) {
      console.error("Error marking notes as read:", error);
      // We don't throw here to avoid blocking UI if this fails
    }
  },

  deleteUser: async (userId: string): Promise<void> => {
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("Not authenticated");
    const token = await currentUser.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to delete user');
    }
  },

  updateUserConfig: async (userId: string, config: Partial<UserConfig>): Promise<User> => {
    await db.collection("configs").doc(userId).set(config, { merge: true });
    return api.getUser(userId);
  },

  updateUserProfile: async (userId: string, profile: UserProfile): Promise<User> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/users/${userId}/profile`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profile)
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Failed to update profile');
    }

    return response.json();
  },

  updateClientAssignments: async (clientId: string, assignedAgentIds: string[]): Promise<void> => {
    console.log(`[api] Updating assignments for client ${clientId} to:`, assignedAgentIds);
    await db.collection("users").doc(clientId).set({
      assignedAgentIds: assignedAgentIds
    }, { merge: true });
  },

  validateVat: async (vatNumber: string, countryCode: string = 'GR'): Promise<{ valid: boolean; name?: string; address?: string }> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/check-vat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ vatNumber, countryCode })
    });

    if (!response.ok) {
      throw new Error('VAT Validation failed');
    }

    return response.json();
  },

  updateUserPreferences: async (userId: string, preferences: { timezone?: string; currency?: string; dateFormat?: string; language?: string; }): Promise<void> => {
    await db.collection("users").doc(userId).set(preferences, { merge: true });
  },

  // Upload file to Firebase Storage
  uploadFile: async (path: string, file: File): Promise<string> => {
    const ref = storage.ref().child(path);
    await ref.put(file);
    return await ref.getDownloadURL();
  },

  getPublicUserConfig: async (userId: string): Promise<{ config: UserConfig | null, status: string, emailJsConfig?: EmailJsConfig }> => {
    try {
      const userSnap = await db.collection("users").doc(userId).get();
      if (!userSnap.exists) {
        return { config: null, status: 'not_found' };
      }
      const userData = userSnap.data();

      // Check Agency Status if applicable
      let status = userData?.subscriptionStatus || 'expired';
      if (userData?.agencyId) {
        const agencySnap = await db.collection("users").doc(userData.agencyId).get();
        if (agencySnap.exists) {
          const agencyData = agencySnap.data();
          if (agencyData?.subscriptionStatus !== 'active') {
            status = 'expired'; // If agency expires, client expires
          }
        }
      }

      if (status !== 'active') {
        return { config: null, status: status };
      }
      const configSnap = await db.collection("configs").doc(userId).get();

      // Fetch public EmailJS settings from global settings or env
      let emailJsConfig: EmailJsConfig | undefined;
      const envServiceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
      const envTemplateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
      const envPublicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

      // Prioritize Env Vars
      if (envServiceId && envTemplateId && envPublicKey) {
        emailJsConfig = {
          serviceId: envServiceId,
          templateId: envTemplateId,
          publicKey: envPublicKey
        };
      } else {
        // Fallback to Database
        try {
          const settingsSnap = await db.collection("settings").doc("global").get();
          if (settingsSnap.exists) {
            const data = settingsSnap.data() as SystemSettings;
            if (data.emailJsServiceId && data.emailJsTemplateId && data.emailJsPublicKey) {
              emailJsConfig = {
                serviceId: data.emailJsServiceId,
                templateId: data.emailJsTemplateId,
                publicKey: data.emailJsPublicKey
              };
            }
          }
        } catch (e) {
          console.warn("Could not fetch global settings for EmailJS");
        }
      }

      if (configSnap.exists) {
        return { config: configSnap.data() as UserConfig, status: 'active', emailJsConfig };
      }
      return { config: null, status: 'config_missing' };
    } catch (e) {
      console.error("Error fetching public config", e);
      return { config: null, status: 'error' };
    }
  },

  // --- NOTIFICATIONS ---
  notifyClient: async (documentId: string, clientId: string, title: string, body: string): Promise<void> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    try {
      const response = await fetch(`${API_BASE_URL}/api/notifications/document`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ documentId, clientId, title, body })
      });

      if (!response.ok) {
        const textRaw = await response.text();
        let errMessage = 'Failed to send notification';
        try {
          const errJson = JSON.parse(textRaw);
          errMessage = errJson.error || errMessage;
        } catch (e) {
          // Not JSON
          errMessage = `Server Error (${response.status}): ${textRaw.substring(0, 200)}...`;
        }
        throw new Error(errMessage);
      }
    } catch (e: any) {
      console.error("notifyClient failed:", e);
      throw e;
    }
  },

  // --- STRIPE NATIVE ---

  validateCoupon: async (code: string) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("No auth token");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/validate-coupon`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ code })
    });
    return response.json();
  },

  createCheckoutSession: async (plan: string, interval: string, couponCode?: string) => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("No auth token");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        plan,
        interval,
        successUrl: window.location.origin + window.location.pathname + '?session_id={CHECKOUT_SESSION_ID}',
        cancelUrl: window.location.href,
        couponCode
      })
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || response.statusText);
    }

    return response.json();
  },

  // --- ADMIN COUPONS (Stripe) ---

  getAdminCoupons: async () => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const response = await fetch(`${API_BASE_URL}/api/admin/coupons`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to fetch coupons');
    return response.json();
  },

  createAdminCoupon: async (code: string, percentOff: number) => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const response = await fetch(`${API_BASE_URL}/api/admin/coupons`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ code, percentOff })
    });
    if (!response.ok) throw new Error('Failed to create coupon');
    return response.json();
  },

  deleteAdminCoupon: async (id: string) => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const response = await fetch(`${API_BASE_URL}/api/admin/coupons/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Failed to delete coupon');
    return response.json();
  },

  updateSubscription: async (userId: string, plan: PlanType, interval: string = 'monthly'): Promise<void> => {
    const token = await auth.currentUser?.getIdToken();
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const response = await fetch(`${API_BASE_URL}/api/update-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ plan, interval })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to update subscription');
    }
  },

  updateUserStatus: async (userId: string, status: 'active' | 'expired' | 'cancelled' | 'inactive'): Promise<void> => {
    await db.collection("users").doc(userId).update({ subscriptionStatus: status });
  },

  extendSubscription: async (userId: string, months: number): Promise<User> => {
    const userRef = db.collection("users").doc(userId);
    const snap = await userRef.get();
    const currentEnd = new Date(snap.data()?.subscriptionEnd || Date.now());
    currentEnd.setMonth(currentEnd.getMonth() + months);
    await userRef.update({
      subscriptionStatus: 'active',
      subscriptionEnd: currentEnd.toISOString().split('T')[0]
    });
    return {} as any;
  },

  getSystemSettings: async (): Promise<SystemSettings> => {
    const docSnap = await db.collection("settings").doc("global").get();
    const envSettings = {
      emailJsServiceId: import.meta.env.VITE_EMAILJS_SERVICE_ID || '',
      emailJsTemplateId: import.meta.env.VITE_EMAILJS_TEMPLATE_ID || '',
      emailJsPublicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY || ''
    };

    if (docSnap.exists) {
      const data = docSnap.data() as SystemSettings;
      return {
        ...data,
        ...envSettings, // Override with env vars if present, or fallback logic if empty? 
        // Actually, let's prioritize Env Vars if they exist.
        emailJsServiceId: envSettings.emailJsServiceId || data.emailJsServiceId || '',
        emailJsTemplateId: envSettings.emailJsTemplateId || data.emailJsTemplateId || '',
        emailJsPublicKey: envSettings.emailJsPublicKey || data.emailJsPublicKey || ''
      };
    }
    return {
      aiApiKey: '',
      ...envSettings
    };
  },

  updateSystemSettings: async (settings: SystemSettings): Promise<void> => {
    await db.collection("settings").doc("global").set(settings, { merge: true });
  },

  getGlobalPricing: async (): Promise<GlobalPricing | null> => {
    try {
      const docSnap = await db.collection("settings").doc("pricing").get();
      if (docSnap.exists) {
        return docSnap.data() as GlobalPricing;
      }
      return {
        [PlanType.LIGHT]: { monthly: 19, yearly: 190 },
        [PlanType.ADVANCED]: { monthly: 49, yearly: 490 },
        [PlanType.AGENCY]: { monthly: 199, yearly: 1990 }
      };
    } catch (error) {
      console.error("Error fetching pricing:", error);
      return null;
    }
  },

  updateGlobalPricing: async (pricing: GlobalPricing): Promise<void> => {
    await db.collection("settings").doc("pricing").set(pricing, { merge: true });
  },

  // --- Appointments ---

  bookAppointment: async (appointment: Omit<Appointment, 'id' | 'createdAt' | 'status'>) => {
    try {
      // 1. Save to Firestore
      const newAppt = {
        ...appointment,
        createdAt: new Date().toISOString(),
        status: 'confirmed'
      };
      await db.collection("appointments").add(newAppt);

      // 2. Log Analytics
      await api.logAnalyticsEvent(appointment.userId, 'booking_request', `${appointment.type} on ${appointment.date} @ ${appointment.time}`);

      return true;
    } catch (error) {
      console.error("Error booking appointment:", error);
      throw error;
    }
  },

  getAppointments: async (userId: string): Promise<Appointment[]> => {
    try {
      const snapshot = await db.collection("appointments")
        .where("userId", "==", userId)
        .get();

      const appointments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Appointment));

      // Sort by Date Descending
      appointments.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      return appointments;
    } catch (error) {
      console.error("Error fetching appointments:", error);
      return [];
    }
  },

  updateUser: async (userId: string, data: any): Promise<void> => {
    try {
      // Sync config to 'configs' collection if present
      // Handle both direct 'config' object AND dot notation 'config.field'
      let configUpdate: any = {};

      if (data.config) {
        configUpdate = data.config;
      }

      // Extract keys starting with 'config.'
      Object.keys(data).forEach(key => {
        if (key.startsWith('config.')) {
          // e.g. "config.ai_features.document_ai_enabled" -> "ai_features.document_ai_enabled"
          const configKey = key.substring(7);
          configUpdate[configKey] = data[key];
        }
      });

      if (Object.keys(configUpdate).length > 0) {
        // Check if we have nested fields (like ai_features.enabled) that need to be objects for set(merge)
        // OR if we should just use update().
        // set({ 'a.b': val }, {merge: true}) behaves like update for those fields if they exist?
        // Actually, for set(merge:true), standard object structure is better.
        // But Firestore update() takes dot notation.
        // Let's use update() on configs collection if the doc exists, or set() if not?
        // Safest is set({ ...unflattened }, { merge: true }) or just update(configUpdate) if we keep dot notation.
        // Since we are syncing, let's try update() first, catch error (if doc doesn't exist), then set.

        const configRef = db.collection("configs").doc(userId);
        const doc = await configRef.get();

        if (doc.exists) {
          await configRef.update(configUpdate);
        } else {
          // We need to unflatten if we use set? No, set() with merge:true doesn't support dot notation keys as fields?
          // Actually it does NOT support dot notation keys for nested fields in the same way update does for partials?
          // Wait, update({ "ai_features.enabled": true }) works.
          // set({ "ai_features.enabled": true }) might create a field with dots in the name!
          // We must UNFLATTEN for set(), or use update().
          // Since we know doc exists (or we create default), let's ensure it exists.
          await configRef.set({}, { merge: true }); // Ensure exists
          await configRef.update(configUpdate); // Then update using dot notation
        }
      }

      // Use set with merge: true to handle nested fields via dot notation if passed as flattening keys
      // But firestore.update() handles dot notation natively.
      await db.collection("users").doc(userId).update(data);
    } catch (error) {
      console.error("Error updating user:", error);
      throw error;
    }
  },

  updateAppointmentStatus: async (appointmentId: string, status: 'pending' | 'confirmed' | 'cancelled'): Promise<void> => {
    try {
      await db.collection("appointments").doc(appointmentId).update({ status });
    } catch (error) {
      console.error("Error updating appointment status:", error);
      throw error;
    }
  },

  // --- Analytics ---

  logAnalyticsEvent: async (userId: string, eventType: 'email_form' | 'phone_click' | 'widget_open' | 'booking_request' | 'call_back' | 'external_link_click', details: string) => {
    // Determine context safely
    const isBrowser = typeof window !== 'undefined';
    let source = 'direct', medium = 'none', campaign = '(not set)', referrer = '', path = '';

    if (isBrowser) {
      const params = new URLSearchParams(window.location.search);
      source = params.get('utm_source') || 'direct';
      medium = params.get('utm_medium') || 'none';
      campaign = params.get('utm_campaign') || '(not set)';
      referrer = document.referrer || '';
      path = window.location.pathname;
    }

    const eventData: Omit<AnalyticsEvent, 'id'> = {
      userId,
      eventType,
      details,
      timestamp: new Date().toISOString(),
      source,
      medium,
      campaign,
      referrer,
      path
    };

    try {
      await db.collection("analytics").add(eventData);
    } catch (e) {
      console.error("Failed to log analytics", e);
    }
  },

  getAnalyticsEvents: async (userId: string, startDate?: Date, endDate?: Date): Promise<AnalyticsEvent[]> => {
    try {
      const snapshot = await db.collection("analytics")
        .where("userId", "==", userId)
        .get();

      let events = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as AnalyticsEvent));

      if (startDate && endDate) {
        const s = new Date(startDate); s.setHours(0, 0, 0, 0);
        const e = new Date(endDate); e.setHours(23, 59, 59, 999);

        events = events.filter(ev => {
          const t = new Date(ev.timestamp);
          return t >= s && t <= e;
        });
      }

      events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return events;
    } catch (e) {
      console.error("Error fetching analytics", e);
      return [];
    }
  },

  // Send Test Analytics Report
  sendTestReport: async (toEmail: string, companyName: string, stats: any, dateRange?: string) => {
    // 1. Check if System Settings are loaded (We need EmailJS credentials OR confirm we use backend)
    // For now, we assume settings are loaded in global state or fetched.
    // Ideally, we fetch them here if missing, but for now we rely on the component passing config or checking settings elsewhere.
    // In this singleton, we can access the 'settings' variable if it acts as a cache, or fetch it.
    // Let's assume 'settings' is populated or we fetch it.
    // Note: The original code accessed 'settings' variable which was likely in closure or module scope.
    // We will ensure we have settings.
    let settings: SystemSettings = { aiApiKey: '', emailJsServiceId: '', emailJsTemplateId: '', emailJsPublicKey: '' };

    /*
      logic to ensure settings are loaded...
    */
    // Fetch System Settings to Check for EmailJS Config
    try {
      const docSnap = await db.collection("settings").doc("global").get();
      if (docSnap.exists) {
        settings = docSnap.data() as SystemSettings;
      }
    } catch (e) {
      console.warn("Could not access system configuration for report.", e);
    }

    if (!settings.emailJsServiceId && !auth.currentUser) {
      // Try fetch if not present
      try {
        const s = await db.collection('settings').doc('global').get(); // Corrected collection name from system_settings to settings
        if (s.exists) {
          const data = s.data() as SystemSettings;
          settings.emailJsServiceId = data.emailJsServiceId;
          settings.emailJsTemplateId = data.emailJsTemplateId;
          settings.emailJsPublicKey = data.emailJsPublicKey;
        }
      } catch (e) {
        console.warn("Could not load system settings for report", e);
      }
    }

    if (!settings.emailJsServiceId && !auth.currentUser) {
      throw new Error("Could not access system configuration.");
    }

    // 2. Try Backend FIRST (Preferred for HTML Template)
    try {
      // GET FIREBASE TOKEN
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("User must be logged in to send reports via backend.");

      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/send-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ toEmail, companyName, stats, dateRange })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send report');
      return data;
    } catch (backendError: any) {
      console.warn("Backend Send Failed, trying EmailJS Fallback:", backendError);

      // 3. Fallback to Client-Side EmailJS
      if (settings.emailJsServiceId && settings.emailJsTemplateId && settings.emailJsPublicKey) {

        // Ensure EmailJS script is loaded
        if (typeof window !== 'undefined' && !(window as any).emailjs) {
          await new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = "https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js";
            script.onload = resolve;
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        if ((window as any).emailjs) {
          const message = `
ANALYTICS REPORT: ${companyName}
--------------------------------
Widget Opens: ${stats.opens}
Appointments: ${stats.appointments}
Messages:     ${stats.emails}
Phone Clicks: ${stats.clicks}

Login to your dashboard for full details.
               `.trim();

          try {
            await (window as any).emailjs.send(
              settings.emailJsServiceId,
              settings.emailJsTemplateId,
              {
                to_email: toEmail,
                from_email: 'reports@widgetflow.ai',
                message: message,
                company_name: companyName,
                subject: `Analytics Report: ${companyName}`
              },
              settings.emailJsPublicKey
            );
            return { success: true, method: 'emailjs_fallback' };
          } catch (err: any) {
            console.error("EmailJS Client Error:", err);
            throw new Error("Failed to send via Backend AND EmailJS.");
          }
        }
      }
      throw backendError;
    }
  },

  // Generic Send Email (Backend SMTP preferred, EmailJS Fallback)
  sendContactEmail: async (toEmail: string, subject: string, message: string, fromEmail?: string): Promise<any> => {
    // 1. Try Backend FIRST
    try {
      // GET FIREBASE TOKEN (if logged in)
      // Note: Backend endpoint requires auth. If user is visitor, this will fail/skip to fallback (unless we open endpoint)
      // For now, only authenticated users (e.g. admins using Preview) can use SMTP here?
      // Or we can modify backend to allow unauthenticated generic emails FROM THE WIDGET?
      // Strict security: Backend requires 'authenticatUser'.
      // So this will throw if !currentUser.
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error("Authentication required for SMTP sending.");

      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ toEmail, subject, message, fromEmail })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send email via backend');
      return { success: true, method: 'backend_smtp' };

    } catch (backendError: any) {
      console.warn("Backend SMTP Send Failed, trying EmailJS Fallback if configured.", backendError);

      // 2. Fetch Settings for Fallback
      let settings: SystemSettings = { aiApiKey: '', emailJsServiceId: '', emailJsTemplateId: '', emailJsPublicKey: '' };
      try {
        const docSnap = await db.collection("settings").doc("global").get();
        if (docSnap.exists) settings = docSnap.data() as SystemSettings;
      } catch (e) {
        // ignore
      }

      if (settings.emailJsServiceId && settings.emailJsTemplateId && settings.emailJsPublicKey) {
        // Ensure script
        if (typeof window !== 'undefined' && !(window as any).emailjs) {
          // ... load script (simplified for brevity, main app usually loads it)
          await new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = "https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js";
            script.onload = resolve;
            document.head.appendChild(script);
          });
        }

        if ((window as any).emailjs) {
          await (window as any).emailjs.send(
            settings.emailJsServiceId,
            settings.emailJsTemplateId,
            {
              to_email: toEmail,
              from_email: fromEmail || 'noreply@widgetflow.ai',
              message: message,
              subject: subject,
              company_name: 'WidgetFlow' // Generic
            },
            settings.emailJsPublicKey
          );
          return { success: true, method: 'emailjs_fallback' };
        }
      }

      throw backendError; // Throw original error if fallback also fails/missing
    }
  },

  // --- STRIPE SUBSCRIPTIONS ---



  createPortalSession: async (): Promise<{ url: string }> => {
    const token = await auth.currentUser?.getIdToken(true);
    if (!token) throw new Error("User must be logged in");

    // Use environment variable for API URL (production) or default to localhost (development)
    // const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/create-portal-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        returnUrl: window.location.href
      })
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to create portal session');
    return data;
  },

  cancelSubscription: async (): Promise<void> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("User must be logged in");

    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to cancel subscription');
    }
  },

  // --- BILLING PROFILE & INVOICES ---

  saveBillingProfile: async (profile: Omit<BillingProfile, 'id' | 'vatValid' | 'taxStatus'>): Promise<BillingProfile> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("User must be logged in");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/billing-profile`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(profile)
    });

    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Failed to save billing profile');
    return data;
  },

  getBillingProfiles: async (userId: string): Promise<BillingProfile[]> => {
    // Option A: Use API (if we want to use the one we built)
    // Option B: Direct Firestore
    // Let's use Direct Firestore for read speed in client, assuming rules allow read.
    // However, I implemented GET /api/billing-profile in server.js.
    // Let's use the API to ensure we get any computed fields or logic if added later.
    // Also consistent with save.

    // Actually, for better UX (realtime updates?), Firestore is better. But requirements didn't ask for realtime.
    // Let's use the API I built.

    const token = await auth.currentUser?.getIdToken();
    if (!token) return []; // or throw
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    try {
      const response = await fetch(`${API_BASE_URL}/api/billing-profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch');
      return await response.json();
    } catch (e) {
      console.warn("API Fetch failed, trying direct DB fallback needed?");
      // Fallback to direct DB if API fails?
      const snap = await db.collection("users").doc(userId).collection("billing_profiles").get();
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as BillingProfile));
    }
  },

  getPayments: async (userId: string): Promise<Payment[]> => {
    try {
      const snap = await db.collection("users").doc(userId).collection("payments").orderBy("date", "desc").get();
      return snap.docs.map(d => ({ id: d.id, ...d.data() } as Payment));
    } catch (error) {
      console.error("Error fetching payments:", error);
      return [];
    }
  },

  requestInvoice: async (paymentId: string, profileId?: string): Promise<void> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("User must be logged in");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/request-invoice`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ paymentId, profileId })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || 'Failed to request invoice');
    }
  },



  // 3. Storage Upload
  // 3. Storage Upload
  uploadDocumentFile: async (file: File, path: string, metadata?: any): Promise<string> => {
    // path format: documents/{agencyName}/{clientName}/{year}/{type}/{filename}
    const storageRef = storage.ref().child(path);
    if (metadata) {
      await storageRef.put(file, { customMetadata: metadata });
    } else {
      await storageRef.put(file);
    }
    return await storageRef.getDownloadURL();
  },

  getAllBillingProfilesAdmin: async (): Promise<any[]> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("User must be logged in");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/admin/billing-profiles`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (!response.ok) throw new Error('Failed to fetch admin data');
    return await response.json();
  },

  sendDocumentNotification: async (data: {
    clientId: string;
    docTitle?: string;
    docType?: string;
    docTypeId?: string;
    agencyName: string;
    documents?: Array<{
      title: string;
      typeName: string;
      typeId?: string;
      date: string;
    }>
  }): Promise<void> => {
    const token = await auth.currentUser?.getIdToken();
    if (!token) throw new Error("User must be logged in");
    const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    const response = await fetch(`${API_BASE_URL}/api/send-document-notification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(data)
    });



  },

  updateDocumentPaymentStatus: async (docId: string, status: 'paid' | 'pending'): Promise<void> => {
    try {
      await db.collection('documents').doc(docId).update({
        paymentStatus: status,
        updatedAt: new Date().toISOString()
      });
    } catch (e: any) {
      console.error("Error updating document status:", e);
      throw new Error(e.message || 'Failed to update document status');
    }
  },

  getDocumentsByIds: async (docIds: string[]): Promise<Document[]> => {
    if (!docIds || docIds.length === 0) return [];
    // Use Modular SDK 'in' query in batches of 30
    // We will perform parallel requests if > 30

    const dbModular = getFirestore(app);
    const docsRef = collection(dbModular, 'documents');

    const chunks = [];
    for (let i = 0; i < docIds.length; i += 30) {
      chunks.push(docIds.slice(i, i + 30));
    }

    const results = await Promise.all(chunks.map(async (chunk) => {
      const q = query(docsRef, where('__name__', 'in', chunk)); // Query by document ID (__name__)
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Document));
    }));

    return results.flat();
  },

  getPaginatedDocuments: async (
    agencyId: string,
    limitCount: number,
    lastVisible: any = null,
    filters: {
      clientId?: string;
      clientIds?: string[]; // Added support for multiple IDs
      typeId?: string;
      status?: string;
      subStatus?: string;
      uploadedByRole?: string;
      dateFrom?: Date;
      dateTo?: Date;
      hasUnreadNotes?: boolean;
    } = {}
  ): Promise<{ data: Document[]; lastVisible: any; hasMore: boolean; totalCount: number }> => {
    try {
      let q = db.collection('documents')
        .where('agencyId', '==', agencyId)
        .orderBy('createdAt', 'desc');

      if (filters.clientId) {
        q = q.where('clientId', '==', filters.clientId);
      }

      // Handle multiple client IDs (for text search)
      if (filters.clientIds && filters.clientIds.length > 0) {
        // Firestore 'in' limitation: max 30
        const safeIds = filters.clientIds.slice(0, 30);
        q = q.where('clientId', 'in', safeIds);
      }

      if (filters.typeId) {
        q = q.where('typeId', '==', filters.typeId);
      }
      if (filters.status) {
        q = q.where('status', '==', filters.status);
      }
      if (filters.uploadedByRole) {
        q = q.where('uploadedByRole', '==', filters.uploadedByRole);
      }
      if (filters.dateFrom) {
        q = q.where('createdAt', '>=', filters.dateFrom);
      }
      if (filters.dateTo) {
        q = q.where('createdAt', '<=', filters.dateTo);
      }
      if (filters.hasUnreadNotes) {
        q = q.where('metadata.hasUnreadNotesForAgency', '==', true);
      }

      // Count Query using Modular SDK (Compat SDK missing .count())
      const dbModular = getFirestore(app);
      const docsRef = collection(dbModular, 'documents');
      let qCount = query(docsRef, where('agencyId', '==', agencyId));

      if (filters.clientId) qCount = query(qCount, where('clientId', '==', filters.clientId));
      if (filters.clientIds && filters.clientIds.length > 0) qCount = query(qCount, where('clientId', 'in', filters.clientIds.slice(0, 30)));
      if (filters.typeId) qCount = query(qCount, where('typeId', '==', filters.typeId));
      if (filters.status) qCount = query(qCount, where('status', '==', filters.status));
      if (filters.uploadedByRole) qCount = query(qCount, where('uploadedByRole', '==', filters.uploadedByRole));
      if (filters.dateFrom) qCount = query(qCount, where('createdAt', '>=', filters.dateFrom));
      if (filters.dateTo) qCount = query(qCount, where('createdAt', '<=', filters.dateTo));
      if (filters.hasUnreadNotes) qCount = query(qCount, where('metadata.hasUnreadNotesForAgency', '==', true));

      const countSnapshot = await getCountFromServer(qCount);
      const totalCount = countSnapshot.data().count;

      if (lastVisible) {
        q = q.startAfter(lastVisible);
      }

      q = q.limit(limitCount + 1);

      const snapshot = await q.get();
      const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Document));
      const hasMore = docs.length > limitCount;
      if (hasMore) docs.pop();

      return {
        data: docs,
        lastVisible: docs.length > 0 ? snapshot.docs[docs.length - 1] : null,
        hasMore,
        totalCount
      };
    } catch (error) {
      console.error("Error fetching paginated documents:", error);
      throw error;
    }
  }
};

const getDefaultConfig = (email: string, language: string = 'en', companyName: string = 'My Company'): UserConfig => ({
  companyName: companyName,
  websiteUrl: '',
  language: language, // Persist user's preferred language
  description: 'Hi, nice to see you here! 👋',
  contactEmail: email,
  phone1: '',
  phone1Label: 'Phone 1',
  phone2: '',
  phone2Label: 'Phone 2',
  phone3: '',
  phone3Label: 'Phone 3',
  mobile: '',
  mobileLabel: 'Mobile',
  contactViber: '',
  contactMessenger: '',
  googlePlaceId: '',
  googleRating: 5.0,
  googleReviewCount: 1,
  googleReviewsUrl: '',
  themeColor: '#000000',
  widgetClosedBgColor: '#000000',
  widgetClosedIconColor: '#ffffff',
  mainContactBtnBgColor: '#000000',
  mainContactBtnTextColor: '#ffffff',
  faqs: [],
  enableFaq: true, // Default enabled
  addresses: [],
  logoUrl: '',
  googleAnalyticsId: '',
  widgetAlignment: 'right',
  showBranding: true, // Default enabled

  // Tab Labels (Navigation)
  tabLabelHome: 'Home',
  tabLabelWelcome: 'Welcome',
  tabLabelContact: 'Contact',
  tabLabelCallus: 'Call Us',
  tabLabelHelp: 'Help',

  // UI Text Labels (Defaults)
  labelMainContactBtn: 'Contact us',
  labelQuickAnswers: 'Quick answers',
  labelShowAllQuestions: 'Show all questions',
  labelContactTitle: 'Contact us',
  labelLeaveMessageBtn: 'Leave a message',
  labelCallBackBtn: 'Request Call Back', // Default label
  labelCallUsBtn: 'Call Us',
  labelViberBtn: 'Viber Message',
  labelMessengerBtn: 'Messenger',
  labelVisitUsBtn: 'Visit Us',
  labelCallUsTitle: 'Call us',
  labelFaqTitle: 'FAQ',

  // Custom Home Button
  customHomeBtnLabel: '',
  customHomeBtnUrl: '',

  // Booking
  enableBooking: false,
  labelBookingBtn: 'Book Appointment',
  bookingBtnColor: '#ffffff',
  bookingBtnTextColor: '#1e293b',
  labelConsultation: 'Online Consultation',
  labelSession: 'Online Session',
  labelOnsite: 'Appointment On-site', // Default

  // Booking Logic Defaults
  appointmentDuration: 60,
  availableDays: [1, 2, 3, 4, 5], // Mon-Fri
  businessHoursStart: '09:00',
  businessHoursEnd: '17:00',
  businessHoursStartWeekend: '10:00',
  businessHoursEndWeekend: '15:00',

  // Booking Types Defaults
  enableConsultation: true,
  enableSession: true,
  enableOnsite: true,

  // Booking Custom Labels
  labelBookNowTitle: 'Book Now',
  labelSelectType: 'Select an appointment type:',
  descConsultation: 'Video call discussion',
  descSession: 'Full 1 hour session',
  descOnsite: 'Visit us at our office', // Default
  labelSelectDate: 'Select a date:',
  labelSelectTime: 'Select a time for',
  labelFormName: 'Your Name',
  labelFormEmail: 'Your Email',
  labelFormPhone: 'Your Phone',
  labelFormMessage: 'Message (Optional)', // Default
  labelConfirmBookingBtn: 'Confirm Booking',

  // Social Media Defaults
  socialFacebook: '',
  socialInstagram: '',
  socialYoutube: '',
  socialLinkedin: '',
  socialTiktok: '',
  //  enableDocumentNotes: false,
  enableAiAssistant: false, // @deprecated

  // New AI Architecture Defaults
  ai_features: {
    document_ai_enabled: false,
    auto_categorization_enabled: false
  },
  // Welcome Video Defaults
  enableWelcomeMessage: true, // Default enabled
  welcomeVideoType: 'upload',
  welcomeVideoUrl: '',
  welcomeImages: [], // Initialize array

  // Analytics Reports
  enableEmailReports: false,
  reportFrequency: 'weekly'
});
